import { observer } from "mobx-react";
import { Box, Card, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Button, LinkButton } from "@/components-v2/Button";
import { MSelect } from "@/components-v2/Select";
import { MTextInput } from "@/components-v2/TextInput";
import { FC, Fragment, useEffect, useMemo } from "react";
import {
  CustomShareClass,
  ShareClassModel,
  ShareTypeNameModel,
  submitShareType,
  toString,
} from "./shareClassesDomain";
import DefaultLayout from "@/containers/DefaultLayout/DefaultLayout";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import {
  useDispatch,
  useRootStore,
  useShareholdersStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { ShareTypeCode } from "@/common/share";
import { languageLabels } from "@/i18n/languages";
import { Alert } from "@/components-v2/Alert";

export const ShareClassInitializer: FC<{ shareTypeId?: number }> = observer(({ shareTypeId }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const { activeShareTypes, shareTypeCodes, userStore, loadAllShareholders } =
    useShareholdersStore();
  const isEditMode = shareTypeId !== undefined;
  const dispatch = useDispatch();

  useEffect(() => {
    loadAllShareholders();
  }, [loadAllShareholders]);

  const model = useMemo(() => {
    if (shareTypeCodes.length > 0) {
      const editValue = isEditMode
        ? activeShareTypes.find((s) => s.sharetypeId === shareTypeId)
        : undefined;
      return new ShareClassModel({
        orgNo: userStore.orgNumber,
        availableShareTypes: shareTypeCodes.map((s) => s as ShareTypeCode),
        existingShareTypes: activeShareTypes,
        editValue,
      });
    } else {
      return undefined;
    }
  }, [shareTypeCodes, isEditMode, activeShareTypes, userStore.orgNumber, shareTypeId]);

  const submit = () => {
    if (model?.validate()) {
      dispatch(submitShareType(model.prepareData(), shareTypeId));
    }
  };

  return model ? (
    <DefaultLayout maxWidth={"lg"} gap={"xl"}>
      <VBox alignItems={"start"}>
        <LinkButton label={t("back")} arrow={"left"} onClick={history.goBack} />
        <h2>
          {isEditMode
            ? t("shareClasses.shareClassForm.edit")
            : t("shareClasses.shareClassForm.create")}
        </h2>
      </VBox>
      <NewOrEditShareClassView model={model} isEditMode={isEditMode} />
      <Box marginLeft={"auto"}>
        <Button variant={"secondary"} label={t("cancel")} onClick={history.goBack} />
        <Button variant={"primary"} label={t("save")} type={"submit"} onClick={() => submit()} />
      </Box>
    </DefaultLayout>
  ) : (
    <LoadingIndicator />
  );
});

export const NewOrEditShareClassView: FC<{
  model: ShareClassModel;
  isEditMode: boolean;
}> = observer(({ model, isEditMode }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <VBox gap={"lg"}>
        <MSelect model={model.shareClass} label={t("choose.sharetype")} disabled={isEditMode} />
        <VBox gap={"lg"}>
          {isEditMode ? (
            <VBox>
              <Box>
                {model.useCustomName ? (
                  <Box>
                    {t("shareClasses.customName.heading")}
                    <LinkButton
                      label={{ key: "shareClasses.customName.useStandard" }}
                      icon={"arrow-counter-clockwise"}
                      onClick={() => model.setUseCustomName(false)}
                    />
                  </Box>
                ) : (
                  <Box>
                    <LinkButton
                      label={{ key: "shareClasses.customName.useCustom" }}
                      icon={"plus"}
                      onClick={() => model.setUseCustomName(true)}
                    />
                  </Box>
                )}
              </Box>
              {model.useCustomName && <ShareClassCustomNameForm model={model.shareTypeNames} />}
            </VBox>
          ) : (
            <VBox>
              {model.shareClass.value === CustomShareClass && (
                <VBox>
                  <ShareClassCustomNameForm model={model.shareTypeNames} />
                  <MSelect
                    label={{ key: "shareClasses.reportingShareType.label" }}
                    model={model.reportingShareClass}
                  />
                  <Alert
                    message={{ key: "shareClasses.reportingShareType.alert" }}
                    type={"info"}
                    icon={"info"}
                  />
                </VBox>
              )}
            </VBox>
          )}
        </VBox>
        <Box>
          <Box>
            <MTextInput
              model={model.numberOfVotes}
              label={t("shareClasses.shareClassForm.sharesEquals")}
            />
          </Box>
        </Box>
      </VBox>
    </Card>
  );
});

export const ShareClassCustomNameForm = observer(function ShareClassCustomNameForm({
  model,
}: {
  model: ShareTypeNameModel[];
}) {
  const { t } = useTranslation();
  const { i18n } = useRootStore();
  return (
    <VBox maxWidth={"md"}>
      <Grid cols={6}>
        {model.map((nameModel) => {
          const translationBundle = i18n.bundles[nameModel.language];
          return (
            <Fragment key={nameModel.language}>
              <GridItem colSpan={3}>
                <MTextInput
                  label={<b>{t(languageLabels[nameModel.language])}</b>}
                  model={nameModel.name}
                />
              </GridItem>
              <GridItem colSpan={2}>
                <MSelect label={{ key: "shareClasses.separator" }} model={nameModel.separator} />
              </GridItem>
              <GridItem>
                <Box fullheight alignItems={"end"} padding={"sm"}>
                  {translationBundle["shareClasses.share"]}
                </Box>
              </GridItem>
              <GridItem colSpan={6}>
                <VBox marginTop={"md"} marginBottom={"md"}>
                  <Box>{t("shareClasses.examples")}</Box>
                  <Box>
                    {`${translationBundle["shareClasses.examples.shareClass"]} ${nameModel.name.validatedValue}`}
                  </Box>
                  <Box>
                    {`1000 ${nameModel.name.validatedValue}${toString(nameModel.separator.value)}${translationBundle["shareClasses.shares"]}`}
                  </Box>
                  <Box>
                    {`1 ${nameModel.name.validatedValue}${toString(nameModel.separator.value)}${translationBundle["shareClasses.share"]}`}
                  </Box>
                </VBox>
              </GridItem>
            </Fragment>
          );
        })}
      </Grid>
    </VBox>
  );
});
